import React from "react";
import { Link } from "react-router-dom";

/**
 * Interface representing a single step in the setup guide.
 */
interface SetupStep {
  number: number; // Step sequence number
  title: string; // Step title (e.g., "Download & Install")
  description: React.ReactNode; // Descriptive content with inline links/nodes
  badgeText?: string; // Optional badge (e.g., "Windows / Mac / Linux")
}

/**
 * Interface representing a single FAQ item.
 */
interface FAQItem {
  question: string; // The query text
  answer: React.ReactNode; // The response text (allows HTML/React nodes for links)
  id: string; // Unique string identifier for query selection/anchors
}

/**
 * DocsPage Component
 *
 * This page serves as the public documentation hub, onboarding guide, and FAQ surface.
 *
 * Design features:
 * - Employs a dark background (`var(--color-bg-primary)`) and light text (`var(--color-text-primary)`).
 * - Accented lines, active headers, and summary elements hover state use `#00D4FF` (`var(--color-accent)`).
 * - Incorporates HTML `<details>` and `<summary>` tags for accessible, keyboard-friendly FAQ accordions.
 * - Adheres to WCAG contrast standards by using high-contrast text shades and clear focus rings.
 *
 * Non-coders note:
 * - To add new setup steps, edit the objects inside the `SETUP_STEPS` array.
 * - To add new FAQ questions, append items to the `FAQ_ITEMS` array.
 */
export const DocsPage: React.FC = () => {
  // --- SETUP INSTRUCTIONS CONSTANTS ---
  // Steps to guide new users from download to mouse crossover operation:
  const SETUP_STEPS: SetupStep[] = [
    {
      number: 1,
      title: "Get the Desktop Application",
      badgeText: "Client App",
      description: (
        <span>
          Visit the{" "}
          <Link
            to="/download"
            style={{ color: "var(--color-accent)", textDecoration: "underline", fontWeight: 600 }}
            data-testid="link-to-downloads"
          >
            Downloads Page
          </Link>{" "}
          and retrieve the installer package appropriate for your operating system. Install the client on every computer you wish to connect to your shared workspace.
        </span>
      ),
    },
    {
      number: 2,
      title: "Register & Pair Your Devices",
      badgeText: "Registration",
      description: (
        <span>
          Log in to the web console. Navigate to your <strong>Dashboard</strong> and click <strong>"Pair New Device"</strong>. Copy the pairing CLI command generated for your user session, paste it into your computer's terminal running the MultiKeys client, and hit Enter to authorize it.
        </span>
      ),
    },
    {
      number: 3,
      title: "Arrange Your Display Geometry",
      badgeText: "Configuration",
      description: (
        <span>
          Head over to the{" "}
          <Link
            to="/configurator"
            style={{ color: "var(--color-accent)", textDecoration: "underline", fontWeight: 600 }}
            data-testid="link-to-configurator"
          >
            Configurator Page
          </Link>{" "}
          to build a virtual display canvas. Drag and arrange your monitor screens to match their physical setup on your desk. Click <strong>"Save Layout"</strong> to sync configuration reactively.
        </span>
      ),
    },
    {
      number: 4,
      title: "Cross the Border",
      badgeText: "Operations",
      description: (
        <span>
          Simply move your mouse cursor past the outer edge of one display. It will seamlessly crossover and reappear on the adjacent screen. Mouse, keyboard, and copy-paste clipboard buffers are now shared!
        </span>
      ),
    },
  ];

  // --- FAQ DATA CONSTANTS ---
  // Frequently asked questions regarding licensing, network requirements, and security:
  const FAQ_ITEMS: FAQItem[] = [
    {
      id: "pairing",
      question: "How do I pair a secondary machine?",
      answer: (
        <span>
          First, make sure the MultiKeys client application is running on that machine. Go to the web console dashboard, click the "Pair New Device" button to generate a token, and run the command shown in your terminal. This securely ties that computer's hardware UUID to your Clerk account in our Convex database.
        </span>
      ),
    },
    {
      id: "limits",
      question: "What is the maximum number of connected computers?",
      answer: (
        <span>
          Users on our <strong>Free Plan</strong> are capped at a maximum of <strong>2 paired devices</strong> in their active display layout. Upgrading to the <strong>Pro Plan</strong> removes all device limits, enabling unlimited screen grids, advanced edge configurations, and shared clipboard.
        </span>
      ),
    },
    {
      id: "network",
      question: "Do my computers need to be on the same Wi-Fi network?",
      answer: (
        <span>
          Yes, high-speed low-latency crossover streams rely on local network connectivity (TCP/UDP socket streams). The web console handles authentication, billing, licensing, and layout signaling, while the desktop client establishes direct peer connections on your local network to route mouse and keyboard inputs instantly.
        </span>
      ),
    },
    {
      id: "security",
      question: "Is keyboard sharing secure against keyloggers?",
      answer: (
        <span>
          Yes. MultiKeys client communication uses industry-standard TLS encryption for all input events routed between local machines. Key inputs are transmitted directly from the primary computer to the secondary computer over your local subnet, never passing through our cloud servers or the public internet.
        </span>
      ),
    },
    {
      id: "clipboard",
      question: "How does clipboard synchronization work?",
      answer: (
        <span>
          When clipboard sync is enabled in your settings, copying text or files on one machine broadcasts an encrypted buffer swap to all online computers in your layout. As your mouse cursor crosses screen boundaries, the target computer updates its local clipboard buffer, enabling natural Copy-Paste workflows across OS environments.
        </span>
      ),
    },
  ];

  return (
    <div
      style={{
        backgroundColor: "var(--color-bg-primary)",
        color: "var(--color-text-primary)",
        minHeight: "75vh",
        padding: "4rem 2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      data-testid="docs-page"
    >
      {/* Container to restrict maximum reading width for optimal legibility */}
      <div style={{ width: "100%", maxWidth: "800px" }}>
        
        {/* 1. Page Title */}
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <h1 style={{ fontSize: "2.75rem", fontWeight: 800, marginBottom: "1rem" }}>
            Documentation & Support
          </h1>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "1.15rem" }}>
            Learn how to set up MultiKeys, configure your screens, and find answers to common questions.
          </p>
        </div>

        {/* 2. Setup Guide Section */}
        <section
          style={{ marginBottom: "5rem" }}
          aria-labelledby="setup-guide-title"
          data-testid="setup-guide"
        >
          <h2
            id="setup-guide-title"
            style={{
              fontSize: "1.75rem",
              fontWeight: 700,
              marginBottom: "2rem",
              borderBottom: "2px solid var(--color-border)",
              paddingBottom: "0.75rem",
            }}
          >
            🏁 Quick Start Setup Guide
          </h2>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
            {SETUP_STEPS.map((step) => (
              <div
                key={step.number}
                data-testid={`setup-step-${step.number}`}
                style={{
                  display: "flex",
                  gap: "1.5rem",
                  backgroundColor: "var(--color-bg-secondary)",
                  padding: "1.75rem",
                  borderRadius: "var(--border-radius-lg)",
                  border: "1px solid var(--color-border)",
                }}
              >
                {/* Step circle indicator */}
                <div
                  style={{
                    backgroundColor: "var(--color-accent-glow)",
                    border: "2px solid var(--color-accent)",
                    color: "var(--color-accent)",
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  {step.number}
                </div>
                
                {/* Step content */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                    <h3 style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0 }}>
                      {step.title}
                    </h3>
                    {step.badgeText && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          backgroundColor: "var(--color-bg-primary)",
                          color: "var(--color-text-secondary)",
                          padding: "0.15rem 0.5rem",
                          borderRadius: "var(--border-radius-sm)",
                          border: "1px solid var(--color-border)",
                          fontWeight: 500,
                        }}
                      >
                        {step.badgeText}
                      </span>
                    )}
                  </div>
                  <p style={{ color: "var(--color-text-secondary)", lineHeight: 1.6, fontSize: "0.95rem" }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 3. FAQ Accordion Section */}
        <section aria-labelledby="faq-title" data-testid="faq-section">
          <h2
            id="faq-title"
            style={{
              fontSize: "1.75rem",
              fontWeight: 700,
              marginBottom: "2rem",
              borderBottom: "2px solid var(--color-border)",
              paddingBottom: "0.75rem",
            }}
          >
            ❓ Frequently Asked Questions
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {FAQ_ITEMS.map((faq) => (
              <details
                key={faq.id}
                data-testid={`faq-item-${faq.id}`}
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--border-radius-md)",
                  overflow: "hidden",
                }}
              >
                <summary
                  style={{
                    padding: "1.25rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    userSelect: "none",
                    outline: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "color var(--transition-speed) ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = "var(--color-accent)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = "var(--color-text-primary)";
                  }}
                >
                  <span>{faq.question}</span>
                </summary>
                
                <div
                  style={{
                    padding: "1.25rem",
                    borderTop: "1px solid var(--color-border)",
                    backgroundColor: "var(--color-bg-primary)",
                    color: "var(--color-text-secondary)",
                    lineHeight: 1.6,
                    fontSize: "0.95rem",
                  }}
                  data-testid={`faq-answer-${faq.id}`}
                >
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* 4. Troubleshooting Support footer */}
        <div
          style={{
            marginTop: "5rem",
            textAlign: "center",
            padding: "2.5rem 1rem",
            backgroundColor: "var(--color-bg-secondary)",
            borderRadius: "var(--border-radius-lg)",
            border: "1px solid var(--color-border)",
          }}
          data-testid="docs-support-card"
        >
          <h3 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "0.75rem" }}>
            Still Having Trouble?
          </h3>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", marginBottom: "1.5rem", maxWidth: "600px", margin: "0 auto 1.5rem" }}>
            Our team is active on Discord and via email ticket channels. Users on the <strong>Team Plan</strong> and <strong>Pro Plan</strong> get priority VIP support queues.
          </p>
          <a
            href="mailto:support@multikeys.dev"
            style={{
              display: "inline-block",
              backgroundColor: "var(--color-bg-primary)",
              color: "var(--color-accent)",
              border: "1px solid var(--color-accent)",
              padding: "0.65rem 1.75rem",
              borderRadius: "var(--border-radius-md)",
              fontWeight: 600,
              fontSize: "0.9rem",
              transition: "all var(--transition-speed) ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-accent)";
              e.currentTarget.style.color = "var(--color-bg-primary)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-bg-primary)";
              e.currentTarget.style.color = "var(--color-accent)";
            }}
          >
            Contact Customer Support
          </a>
        </div>

      </div>
    </div>
  );
};
